import { useState, useEffect, useCallback, useRef } from "react";
import api, { presignedUpload } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Skeleton } from "../components/ui/skeleton";
import { Palette, Plus, Pencil, Trash2, CheckCircle2, AlertCircle, Phone, Type, Upload, X, Building2, Mail } from "lucide-react";
import { toast } from "sonner";

export default function BrandKits() {
  const [kits, setKits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [phone, setPhone] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [ctaText, setCtaText] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [logoCheck, setLogoCheck] = useState(null);
  const fileRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      const res = await api.get("/brand-kits");
      setKits(res.data);
    } catch { toast.error("Failed to load brand kits"); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreate = () => {
    setEditing(null); setName(""); setCompanyName(""); setPhone(""); setContactEmail(""); setCtaText(""); setFile(null); setPreview(null); setLogoCheck(null); setShowDialog(true);
  };
  const openEdit = (k) => {
    setEditing(k); setName(k.name); setCompanyName(k.company_name || ""); setPhone(k.phone || ""); setContactEmail(k.contact_email || ""); setCtaText(k.cta_text || "");
    setFile(null); setPreview(k.logo_url || null); setLogoCheck(k.logo_checks || null); setShowDialog(true);
  };

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".png")) {
      toast.error("Logo must be in PNG format");
      return;
    }
    setFile(f);
    setPreview(URL.createObjectURL(f));
    setLogoCheck(null);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".png")) {
      toast.error("Logo must be in PNG format");
      return;
    }
    setFile(f);
    setPreview(URL.createObjectURL(f));
    setLogoCheck(null);
  };

  const handleSave = async () => {
    if (!name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      if (file) {
        // Upload file directly to S3 via presigned URL (bypasses API Gateway 10MB limit)
        const { s3_url } = await presignedUpload(file);
        const payload = { name, logo_url: s3_url, company_name: companyName, phone, contact_email: contactEmail, cta_text: ctaText };
        let res;
        if (editing) {
          res = await api.put(`/brand-kits/${editing.id}/update-from-url`, payload);
        } else {
          res = await api.post("/brand-kits/create-from-url", payload);
        }
        setLogoCheck(res.data.logo_checks);
        if (res.data.logo_checks?.passed) {
          toast.success(editing ? "Brand kit updated" : "Brand kit created - All checks passed!");
        } else {
          toast.warning("Brand kit saved with logo issues");
        }
      } else if (editing) {
        await api.put(`/brand-kits/${editing.id}`, { name, logo_url: editing.logo_url || "", company_name: companyName, phone, contact_email: contactEmail, cta_text: ctaText });
        toast.success("Brand kit updated");
      } else {
        toast.error("Please upload a logo (PNG)"); setSaving(false); return;
      }
      setShowDialog(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save brand kit");
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/brand-kits/${id}`);
      setKits(prev => prev.filter(k => k.id !== id));
      toast.success("Brand kit deleted");
    } catch { toast.error("Failed to delete"); }
  };

  return (
    <div className="p-8 max-w-5xl mx-auto" data-testid="brand-kits-page">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <Palette size={24} className="text-[#4D11F6]" strokeWidth={1.5} />
          <div>
            <h1 className="text-3xl font-bold text-[#020B23]" style={{ fontFamily: 'Sora, sans-serif' }}>Brand Kits</h1>
            <p className="text-[#676970] text-sm mt-0.5">Configure brand overlays for video closing</p>
          </div>
        </div>
        <Button onClick={openCreate} data-testid="create-brand-kit-btn"
          className="bg-[#4D11F6] hover:bg-[#3A0BC0] text-white h-10 px-5">
          <Plus size={16} className="mr-2" /> New Brand Kit
        </Button>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {[1,2].map(i => <Skeleton key={i} className="h-40 w-full rounded-xl" />)}
        </div>
      ) : kits.length === 0 ? (
        <div className="flex flex-col items-center py-20 text-center" data-testid="brand-kits-empty">
          <div className="w-14 h-14 rounded-2xl bg-[#4D11F6]/10 flex items-center justify-center mb-4">
            <Palette size={24} className="text-[#4D11F6]" />
          </div>
          <h3 className="text-lg font-semibold text-[#020B23] mb-2" style={{ fontFamily: 'Sora, sans-serif' }}>No brand kits yet</h3>
          <p className="text-[#676970] text-sm mb-5 max-w-xs">Upload a PNG logo with your contact info for video overlays.</p>
          <Button onClick={openCreate} className="bg-[#4D11F6] hover:bg-[#3A0BC0] text-white">
            <Plus size={16} className="mr-2" /> Create Brand Kit
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {kits.map((kit, idx) => (
            <div key={kit.id} data-testid={`brand-kit-card-${kit.id}`}
              className="bg-white rounded-xl border border-[#E2E4E7] p-5 hover:shadow-md transition-shadow animate-fade-in group"
              style={{ animationDelay: `${idx * 40}ms` }}>
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  {kit.logo_url ? (
                    <div className="w-12 h-12 rounded-lg border border-[#E2E4E7] overflow-hidden bg-[#F3F3F9] flex items-center justify-center p-1">
                      <img src={kit.logo_url} alt={kit.name} className="w-full h-full object-contain" onError={(e) => { e.target.style.display = 'none'; }} />
                    </div>
                  ) : (
                    <div className="w-12 h-12 rounded-lg bg-[#F3F3F9] flex items-center justify-center">
                      <Palette size={20} className="text-[#94969C]" />
                    </div>
                  )}
                  <div>
                    <h3 className="font-semibold text-[#020B23]" style={{ fontFamily: 'Sora, sans-serif' }}>{kit.name}</h3>
                    <Badge className={kit.status === 'Valid' ? 'bg-emerald-50 text-emerald-700 border-emerald-100 text-xs mt-1' : 'bg-amber-50 text-amber-700 border-amber-100 text-xs mt-1'}>
                      {kit.status === 'Valid' ? <CheckCircle2 size={11} className="mr-1" /> : <AlertCircle size={11} className="mr-1" />}
                      {kit.status}
                    </Badge>
                  </div>
                </div>
              </div>

              {/* Logo check indicators */}
              {kit.logo_checks && (
                <div className="space-y-1 mb-3">
                  <div className="flex items-center gap-2 text-xs">
                    {kit.logo_checks.has_transparency ?
                      <span className="text-emerald-600 flex items-center gap-1"><CheckCircle2 size={10} /> Transparent</span> :
                      <span className="text-amber-600 flex items-center gap-1"><AlertCircle size={10} /> No transparency</span>}
                    {kit.logo_checks.min_resolution_ok ?
                      <span className="text-emerald-600 flex items-center gap-1"><CheckCircle2 size={10} /> {kit.logo_checks.width}x{kit.logo_checks.height}</span> :
                      <span className="text-amber-600 flex items-center gap-1"><AlertCircle size={10} /> Low res</span>}
                    <span className="text-emerald-600 flex items-center gap-1"><CheckCircle2 size={10} /> PNG</span>
                  </div>
                </div>
              )}

              <div className="space-y-1.5 mb-4">
                {kit.company_name && <p className="text-sm text-[#676970] flex items-center gap-2"><Building2 size={13} /> {kit.company_name}</p>}
                {kit.phone && <p className="text-sm text-[#676970] flex items-center gap-2"><Phone size={13} /> {kit.phone}</p>}
                {kit.contact_email && <p className="text-sm text-[#676970] flex items-center gap-2"><Mail size={13} /> {kit.contact_email}</p>}
                {kit.cta_text && <p className="text-sm text-[#676970] flex items-center gap-2"><Type size={13} /> {kit.cta_text}</p>}
              </div>
              <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <Button variant="outline" size="sm" onClick={() => openEdit(kit)} className="text-xs border-[#E2E4E7]">
                  <Pencil size={12} className="mr-1" /> Edit
                </Button>
                <Button variant="outline" size="sm" onClick={() => handleDelete(kit.id)} className="text-xs border-[#E2E4E7] text-[#F5354E] hover:bg-red-50">
                  <Trash2 size={12} className="mr-1" /> Delete
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-lg bg-white" data-testid="brand-kit-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Sora, sans-serif' }} className="text-[#020B23]">
              {editing ? "Edit Brand Kit" : "Create Brand Kit"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="text-[#1A2238] font-medium">Brand Name</Label>
              <Input placeholder="e.g., Sunset Realty" value={name} onChange={(e) => setName(e.target.value)}
                data-testid="brand-name-input" className="h-10 bg-white border-[#E2E4E7]" />
            </div>

            {/* Logo Upload */}
            <div className="space-y-2">
              <Label className="text-[#1A2238] font-medium">Logo (PNG only)</Label>
              <div
                onClick={() => fileRef.current?.click()}
                onDrop={handleDrop}
                onDragOver={(e) => e.preventDefault()}
                data-testid="brand-logo-upload"
                className={`relative border-2 border-dashed rounded-xl cursor-pointer transition-colors ${
                  preview ? "border-[#4D11F6]/30 bg-[#4D11F6]/5 p-3" : "border-[#E2E4E7] hover:border-[#4D11F6]/40 bg-[#F3F3F9] p-6"
                }`}
              >
                {preview ? (
                  <div className="relative flex items-center justify-center">
                    <img src={preview} alt="Logo preview" className="h-20 object-contain" style={{ background: 'repeating-conic-gradient(#E2E4E7 0% 25%, transparent 0% 50%) 50% / 12px 12px' }} />
                    <button
                      onClick={(e) => { e.stopPropagation(); setFile(null); setPreview(null); setLogoCheck(null); }}
                      className="absolute top-0 right-0 w-6 h-6 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80"
                    >
                      <X size={12} />
                    </button>
                  </div>
                ) : (
                  <div className="text-center">
                    <Upload size={24} className="mx-auto text-[#94969C] mb-2" />
                    <p className="text-sm font-medium text-[#1A2238]">Drop PNG logo or click to upload</p>
                    <p className="text-xs text-[#94969C] mt-1">Must be PNG, transparent background recommended, min 200x200</p>
                  </div>
                )}
                <input ref={fileRef} type="file" accept=".png,image/png" className="hidden" onChange={handleFileChange} />
              </div>
            </div>

            {/* Logo Check Results */}
            {logoCheck && (
              <div className={`rounded-lg p-4 border ${logoCheck.passed ? "bg-emerald-50 border-emerald-100" : "bg-amber-50 border-amber-100"}`}
                data-testid="logo-quality-results">
                <p className={`text-sm font-medium mb-2 ${logoCheck.passed ? "text-emerald-700" : "text-amber-700"}`}>
                  {logoCheck.passed ? "All logo checks passed" : "Logo issues detected"}
                </p>
                <div className="space-y-1.5">
                  <div className="flex items-center gap-2 text-xs">
                    <CheckCircle2 size={12} className="text-emerald-600" />
                    <span className="text-emerald-700">Format: PNG</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    {logoCheck.has_transparency ?
                      <><CheckCircle2 size={12} className="text-emerald-600" /><span className="text-emerald-700">Transparent background detected</span></> :
                      <><AlertCircle size={12} className="text-amber-600" /><span className="text-amber-700">No transparency - consider using a transparent PNG</span></>}
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    {logoCheck.min_resolution_ok ?
                      <><CheckCircle2 size={12} className="text-emerald-600" /><span className="text-emerald-700">Resolution: {logoCheck.width}x{logoCheck.height} (OK)</span></> :
                      <><AlertCircle size={12} className="text-amber-600" /><span className="text-amber-700">Resolution: {logoCheck.width}x{logoCheck.height} (min 200x200)</span></>}
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label className="text-[#1A2238] font-medium">Company Name</Label>
              <Input placeholder="e.g., AAIRE Real Estate" value={companyName} onChange={(e) => setCompanyName(e.target.value)}
                data-testid="brand-company-input" className="h-10 bg-white border-[#E2E4E7]" />
            </div>
            <div className="space-y-2">
              <Label className="text-[#1A2238] font-medium">Phone Number</Label>
              <Input placeholder="+1 (555) 123-4567" value={phone} onChange={(e) => setPhone(e.target.value)}
                data-testid="brand-phone-input" className="h-10 bg-white border-[#E2E4E7]" />
            </div>
            <div className="space-y-2">
              <Label className="text-[#1A2238] font-medium">Contact Email</Label>
              <Input placeholder="info@company.com" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)}
                data-testid="brand-email-input" className="h-10 bg-white border-[#E2E4E7]" />
            </div>
            <div className="space-y-2">
              <Label className="text-[#1A2238] font-medium">CTA Text</Label>
              <Input placeholder="e.g., BOOK A VIEWING" value={ctaText} onChange={(e) => setCtaText(e.target.value)}
                data-testid="brand-cta-input" className="h-10 bg-white border-[#E2E4E7]" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)} className="border-[#E2E4E7]">Cancel</Button>
            <Button onClick={handleSave} disabled={saving || !name.trim()} data-testid="save-brand-kit-btn"
              className="bg-[#4D11F6] hover:bg-[#3A0BC0] text-white">
              {saving ? "Uploading & checking..." : editing ? "Update" : "Create Brand Kit"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
